import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { streamAI } from "@/lib/stream";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

export default function AIChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && user && user !== false && messages.length === 0) {
      api.get("/ai/chat/history").then(r => setMessages(r.data.messages)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [open, user]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (!user || user === false) return null;

  const send = async (e) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || busy) return;
    setInput(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      await streamAI("/ai/chat", {
        method: "POST",
        body: { message: msg },
        onDelta: (t) => setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + t };
          return c;
        }),
      });
    } catch (_) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry, something went wrong." }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="ai-chat-fab" onClick={() => setOpen(true)}
              className={`fixed bottom-5 right-5 z-50 h-12 w-12 rounded-full shadow-xl grid place-items-center btn-earth ${open ? "hidden" : ""}`}>
        <MessageCircle className="h-5 w-5" />
      </button>
      {open && (
        <div data-testid="ai-chat-panel" className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm h-[520px] bg-white border border-stone-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between bg-[color:var(--forest-deep)] text-stone-100">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[color:var(--earth-amber)]" />
              <div>
                <div className="heading text-sm font-semibold">GeneticsTrade Advisor</div>
                <div className="text-[10px] mono uppercase text-stone-300">Claude Sonnet 5 · breeds &amp; listings</div>
              </div>
            </div>
            <button data-testid="ai-chat-close-btn" onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-sm text-stone-500 text-center mt-8">
                Ask about heritage breeds, conservation status, listings, or cold-chain handling.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`ai-chat-msg-${i}`} className={`max-w-[85%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[color:var(--forest-deep)] text-stone-100" : "bg-stone-100 text-stone-800"}`}>
                  {m.content || (busy && i === messages.length - 1 ? <span className="inline-block w-2 h-4 bg-stone-400 animate-pulse" /> : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-stone-200 flex gap-2">
            <Input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about a breed or lot…" />
            <Button data-testid="ai-chat-send-btn" type="submit" disabled={busy || !input.trim()} className="btn-forest px-3"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
}
